import type {
  MyOrganizationResponse,
  OnboardingResponse,
  OrganizationResponse,
  PaginationMeta,
} from '../types/models.js'
import { API_BASE } from './client.js'
import { ApiError } from '../types/api.js'

function token(): string | null {
  return localStorage.getItem('mf_access')
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = token()
  if (t) headers['Authorization'] = `Bearer ${t}`
  return headers
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    // non-JSON error response
  }
  const errBody = (body as { error?: { code: string; message: string } } | null)?.error
    ?? { code: 'UNKNOWN', message: fallback }
  return new ApiError(res.status, errBody)
}

async function requestJson<T>(path: string, init: RequestInit, fallback: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, fallback)
  if (res.status === 204) return null as T
  return res.json() as Promise<T>
}

export interface OrganizationListQuery {
  page?: number
  pageSize?: number
  search?: string
  status?: string
}

export interface OrganizationListResult {
  data: OrganizationResponse[]
  meta: PaginationMeta
}

export type StorefrontUnavailableReason =
  | 'OK'
  | 'ORG_NOT_ACTIVE'
  | 'NO_ACTIVE_LOCATIONS'
  | 'NO_ACTIVE_CITY'

export interface PlatformStorefrontUrl {
  cityId: string
  cityName: string
  citySlug: string
  locationCount: number
  path: string
  url: string
}

export interface PlatformStorefrontInfo {
  available: boolean
  reason: StorefrontUnavailableReason
  urls: PlatformStorefrontUrl[]
}

export interface PlatformLocationSummary {
  id: string
  name: string
  slug: string | null
  status: string
  address: string | null
  timezone: string
  city: { id: string; name: string; slug: string; status: string } | null
}

export interface PlatformCatalogCounters {
  categoriesTotal: number
  categoriesActive: number
  productsTotal: number
  productsActive: number
}

export interface PlatformOrganizationDetail {
  organization: OrganizationResponse
  locations: PlatformLocationSummary[]
  counters: PlatformCatalogCounters
  storefront: PlatformStorefrontInfo
}

export async function getPlatformOrganizationDetail(
  organizationId: string,
): Promise<PlatformOrganizationDetail> {
  const body = await requestJson<{ data: PlatformOrganizationDetail }>(
    `/organizations/${encodeURIComponent(organizationId)}/platform-detail`,
    { method: 'GET' },
    'Failed to load organization platform detail',
  )
  return body.data
}

export async function listOrganizations(
  query: OrganizationListQuery = {},
): Promise<OrganizationListResult> {
  const params = new URLSearchParams()
  if (query.page) params.set('page', String(query.page))
  if (query.pageSize) params.set('pageSize', String(query.pageSize))
  if (query.search) params.set('search', query.search)
  if (query.status) params.set('status', query.status)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  return requestJson<OrganizationListResult>(
    `/organizations${suffix}`,
    { method: 'GET' },
    'Failed to load organizations',
  )
}

export async function suspendOrganization(organizationId: string): Promise<OrganizationResponse> {
  const body = await requestJson<{ data: OrganizationResponse }>(
    `/organizations/${encodeURIComponent(organizationId)}/suspend`,
    { method: 'POST' },
    'Failed to suspend organization',
  )
  return body.data
}

export async function activateOrganization(organizationId: string): Promise<OrganizationResponse> {
  const body = await requestJson<{ data: OrganizationResponse }>(
    `/organizations/${encodeURIComponent(organizationId)}/activate`,
    { method: 'POST' },
    'Failed to activate organization',
  )
  return body.data
}

export async function archiveOrganization(organizationId: string): Promise<OrganizationResponse> {
  const body = await requestJson<{ data: OrganizationResponse }>(
    `/organizations/${encodeURIComponent(organizationId)}/archive`,
    { method: 'POST' },
    'Failed to archive organization',
  )
  return body.data
}


export async function getMyOrganizations(): Promise<MyOrganizationResponse[]> {
  const res = await fetch(`${API_BASE}/my/memberships`, { headers: authHeaders() })
  if (!res.ok) {
    const b = await res.json()
    throw new ApiError(res.status, b?.error ?? { code: 'UNKNOWN', message: 'Failed to load organizations' })
  }
  const b = await res.json()
  return b.data
}

export async function createOrganization(data: {
  name: string
  slug?: string
}): Promise<OnboardingResponse> {
  const res = await fetch(`${API_BASE}/organizations`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const b = await res.json()
    throw new ApiError(res.status, b?.error ?? { code: 'UNKNOWN', message: 'Failed to create organization' })
  }
  const b = await res.json()
  return b.data
}